import { Button, Card, Empty, Input, Select, Tag, notification } from "antd";
import { get } from "lodash";
import { useState } from "react";
import { BiPlus, BiTrash } from "react-icons/bi";
import { useMutation } from "shared/hooks/useApi";
import graphql from "utils/api/graphql";
import cuid from "cuid";

const DEPARTMENTS = [
  "01","02","03","04","05","06","07","08","09","10",
  "11","12","13","14","15","16","17","18","19","2A","2B",
  "21","22","23","24","25","26","27","28","29","30",
  "31","32","33","34","35","36","37","38","39","40",
  "41","42","43","44","45","46","47","48","49","50",
  "51","52","53","54","55","56","57","58","59","60",
  "61","62","63","64","65","66","67","68","69","70",
  "71","72","73","74","75","76","77","78","79","80",
  "81","82","83","84","85","86","87","88","89","90",
  "91","92","93","94","95",
  "971","972","973","974","975","977","978",
];

const ZoneManager = ({ zones, zoneType, planId, setFieldValue }) => {
  const [adding, setAdding] = useState(false);
  const [createZone] = useMutation(graphql.mutations.CREATE_ZONE);
  const [updateZone] = useMutation(graphql.mutations.UPDATE_ZONE);
  const [deleteZone] = useMutation(graphql.mutations.DELETE_ZONE);

  const handleAddZone = async () => {
    if (adding) return;
    setAdding(true);
    const id = cuid();
    const newZone = {
      id,
      name: `Zone ${(zones || []).length + 1}`,
      description: null,
      values: [],
      order: (zones || []).length + 1,
    };

    try {
      await createZone({
        variables: {
          data: {
            ...newZone,
            insurancePlan: { id: planId },
          },
        },
      });
      setFieldValue("zones", [...(zones || []), newZone]);
      notification.success({ message: "Zone ajoutée" });
    } catch (err) {
      notification.error({ message: "Échec de l'ajout de la zone" });
    } finally {
      setAdding(false);
    }
  };

  const handleDeleteZone = async (index) => {
    const zone = zones[index];
    try {
      await deleteZone({ variables: { where: { id: zone.id } } });
      setFieldValue(
        "zones",
        zones.filter((_, i) => i !== index),
      );
      notification.success({ message: "Zone supprimée" });
    } catch (err) {
      notification.error({ message: "Échec de la suppression de la zone" });
    }
  };

  const handleLocalChange = (index, field, value) => {
    const updated = [...zones];
    updated[index] = { ...updated[index], [field]: value };
    setFieldValue("zones", updated);
  };

  const handleSaveField = async (index, field) => {
    const zone = zones[index];
    try {
      await updateZone({
        variables: {
          where: { id: zone.id },
          data: { [field]: zone[field] },
        },
      });
    } catch (err) {
      notification.error({ message: "Échec de la mise à jour de la zone" });
    }
  };

  const handleSaveValues = async (index, values) => {
    const zone = zones[index];
    const updated = [...zones];
    updated[index] = { ...updated[index], values };
    setFieldValue("zones", updated);
    try {
      await updateZone({
        variables: {
          where: { id: zone.id },
          data: { values },
        },
      });
    } catch (err) {
      notification.error({ message: "Échec de la mise à jour des valeurs" });
    }
  };

  const renderValuesInput = (zone, index) => {
    switch (zoneType) {
      case "DEPARTMENT":
        return (
          <Select
            mode="multiple"
            placeholder="Sélectionner les départements..."
            value={zone.values || []}
            onChange={(vals) => handleSaveValues(index, vals)}
            style={{ width: "100%" }}
            maxTagCount="responsive"
            showSearch
          >
            {DEPARTMENTS.map((dept) => (
              <Select.Option key={dept} value={dept}>
                {dept}
              </Select.Option>
            ))}
          </Select>
        );
      case "ZIP_CODE":
        return (
          <Select
            mode="tags"
            placeholder="Saisir les codes postaux (5 chiffres)..."
            value={zone.values || []}
            onChange={(vals) => {
              const valid = vals.filter((v) => /^\d{5}$/.test(v));
              if (valid.length !== vals.length) {
                notification.warning({
                  message: "Les codes postaux doivent contenir 5 chiffres",
                });
              }
              handleSaveValues(index, valid);
            }}
            style={{ width: "100%" }}
            maxTagCount="responsive"
            tokenSeparators={[",", " "]}
          />
        );
      case "FREE_FORM":
        return null;
      default:
        return (
          <span style={{ color: "#8A92A5", fontSize: 12 }}>
            Sélectionnez un type de zone ci-dessus
          </span>
        );
    }
  };

  if (!zoneType) {
    return <Empty description="Sélectionnez un type de zone ci-dessus pour commencer." />;
  }

  return (
    <div>
      <div className="d-flex justify--end m-bottom-12">
        <Button
          type="primary"
          size="small"
          icon={<BiPlus size={14} />}
          onClick={handleAddZone}
          loading={adding}
        >
          Ajouter
        </Button>
      </div>
      {(!zones || zones.length === 0) ? (
        <Empty description="Aucune zone définie. Cliquez sur 'Ajouter' pour en créer." />
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
          {zones.map((zone, index) => (
            <Card key={zone.id} size="small" style={{ borderRadius: 6 }}>
              <div
                className="d-flex"
                style={{ gap: "12px", alignItems: "flex-start" }}
              >
                <div style={{ flex: 1 }}>
                  <div
                    className="d-flex"
                    style={{ gap: "8px", marginBottom: 8 }}
                  >
                    <Input
                      size="small"
                      value={zone.name}
                      onChange={(e) =>
                        handleLocalChange(index, "name", e.target.value)
                      }
                      onBlur={() => handleSaveField(index, "name")}
                      style={{ width: 200 }}
                      placeholder="Nom de la zone"
                    />
                    <Tag color="blue">#{zone.order}</Tag>
                  </div>
                  <Input.TextArea
                    size="small"
                    value={zone.description || ""}
                    onChange={(e) =>
                      handleLocalChange(index, "description", e.target.value)
                    }
                    onBlur={() => handleSaveField(index, "description")}
                    placeholder="Description / notes (optionnel)"
                    rows={1}
                    style={{ marginBottom: 8 }}
                  />
                  {renderValuesInput(zone, index)}
                </div>
                <Button
                  type="text"
                  danger
                  size="small"
                  icon={<BiTrash size={16} />}
                  onClick={() => handleDeleteZone(index)}
                />
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default ZoneManager;
